import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AssetCollectionPublicViewService } from './asset-collection-public-view.service';
import { AssetCollectionPublicViewDTO, FileManagerDirectoryContentDTO, IFileManagerDirectoryContentDTO } from 'src/app/base/models/models.swagger.generated';
import { TranslateModule } from '@ngx-translate/core';

import { BeforeDownloadEventArgs, BeforeImageLoadEventArgs, BeforeSendEventArgs, ContextMenuSettingsModel, FileManagerComponent, FileManagerModule, FileOpenEventArgs, FileSelectionEventArgs, ToolbarSettingsModel } from '@syncfusion/ej2-angular-filemanager';
import { IAssetStorageEntry } from '../../asset-storage/asset-storage-entry';
import { map, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ToastService } from 'src/app/base/services/toast.service';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-asset-collection-public-view',
    templateUrl: './asset-collection-public-view.component.html',
    styleUrls: ['./asset-collection-public-view.component.scss'],
    imports: [
    TranslateModule,
    FileManagerModule
]
})

export class AssetCollectionPublicViewComponent implements OnInit {
  private token: string;
  public assetCollection: AssetCollectionPublicViewDTO;
  public invalidLink = false;
  public noAssets = false;
  public view?: any;

  constructor(
    private route: ActivatedRoute,
    private service: AssetCollectionPublicViewService,
    private httpClient: HttpClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService,
    private cdr: ChangeDetectorRef
  ) {
  }

  @ViewChild(FileManagerComponent) public fileManager: FileManagerComponent;

  public navigationPaneSettings = { visible: false };

  public fileSystemData: { [key: string]: Object }[];

  public toolbarSettings: ToolbarSettingsModel = {
    items: ['SelectAll', 'Download'],
    visible: true
  };

  public contextMenuSettings: ContextMenuSettingsModel = {
    file: ['Download'],
    folder: null,
    layout: ['SortBy', '|', 'SelectAll'],
    visible: true,
  };

  private baseUrl: string = environment.apiServerUrl + environment.assetCollectionPublicViewApiRoute + 'FileOperations';
  private baseUrlDownload: string = environment.apiServerUrl + environment.assetCollectionPublicViewApiRoute + 'Download';
  public ajaxSettings: object = {
    url: this.baseUrl,
    downloadUrl: this.baseUrlDownload,
  };

  ngOnInit() {
    this.route.queryParamMap.subscribe(params => {
      this.token = params.get('token');
      if (this.token) {
        this.service.getAssetCollection(this.token).subscribe(
          {
            next: (assetCollection) => {
              this.assetCollection = assetCollection;
              this.noAssets = !this.assetCollection.fileData?.files || this.assetCollection.fileData?.files.length === 0;
              // root folder
              this.initializeFileSystemData();
              this.cdr.detectChanges();
            },
            error: (error) => {
              this.invalidLink = true;
              this.cdr.detectChanges();
            }
          }
        );
      }
    });
  }

  public beforeImageLoad(args: BeforeImageLoadEventArgs): void {
    args.imageUrl = (args.fileDetails?.at(0) as any)?.fileLink ?? 'assets/img/document_unspecified.svg';
  }

  public onFileOpen(args: FileOpenEventArgs) {

    // get the extension of the file
    const asset = args.fileDetails as IAssetStorageEntry;
    if (asset?.isFile) {
      //if file is of type then open in the preview
      const extension = asset.name.toLowerCase().split('.').pop();
      // check if extension is jpg, jpeg, png
      if (['jpg', 'jpeg', 'png'].includes(extension)) {
        // show image in new tab since preview shows image distorted
        args.cancel = true;
        window.open(asset.fileLink, '_blank');
        return; // this will open the preview window
      }
    }
  }

  private initializeFileSystemData() {
    const filesData = [{
      dateCreated: new Date(),
      dateModified: new Date(),
      filterPath: '',
      hasChild: true,
      id: '0',
      isFile: false,
      name: this.assetCollection?.name,
      parentId: undefined,
      type: 'folder',
    }];
    var data = this.assetCollection.fileData?.files?.map((file: FileManagerDirectoryContentDTO) => {
      return {
        id: file.id,
        name: file.name,
        size: file.size,
        dateCreated: new Date(file.dateCreated?.toString()), // file.dateCreated,
        dateModified: new Date(file.dateModified?.toString()), // file.dateModified,
        hasChild: file.hasChild,
        isFile: file.isFile,
        parentId: 0,
        type: file.type,
        filterPath: '\\',
        fileLink: file.fileLink,
        imageUrl: file.fileLink
      };
    });
    if (data) {
      this.noAssets = data.length === 0;
      filesData.push(...data);
    }
    this.fileSystemData = filesData;
  }

  private createInvisibleDownloadLink(url: string, filename: string): Observable<HTMLAnchorElement> {
    // using directly the url in link.href does not work, because the browser then rather opens a new page with the url than downloading the file
    return this.httpClient.get(url, { responseType: 'blob' }).pipe(
      map((result: Blob) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(result);
        link.download = filename;
        return link;
      }));
  }

  public beforeSend(args: BeforeSendEventArgs) {
    let data: IFileManagerDirectoryContentDTO = JSON.parse((args.ajaxSettings as any).data);
    if (args.action === 'create' || args.action === 'rename' || args.action === 'Upload') {
      args.cancel = true;
      return;
    }

    // set the data back to the ajax settings as JSON string
    this.addUrlParameter(args, data, this.baseUrl);

  }

  public beforeDownload(args: BeforeDownloadEventArgs): void {
    args.cancel = true;
    const assetsToDownload = (args.data as any).data as IAssetStorageEntry[] ?? [];
    if (assetsToDownload.length === 1 && assetsToDownload[0].isFile) {
      const asset = assetsToDownload[0];
      if (asset.isFile) {
        const link = this.createInvisibleDownloadLink(asset.fileLink, asset.name).subscribe((link: HTMLAnchorElement) => {
          link.click();
          link.remove();
        }
        );
      }
    }
    else { // multiple assets selected, download them as zip
      let assetIds = assetsToDownload.map((entry: IAssetStorageEntry) => entry.entryId);

      this.service.downloadAssets(this.assetCollection.id, assetIds).subscribe({
        next: (response) => {
          const a = document.createElement('a');
          const objectUrl = URL.createObjectURL(response.data);
          a.href = objectUrl;
          a.download = this.assetCollection.name ?? 'CollectionFiles' + '.zip';
          a.click();
          URL.revokeObjectURL(objectUrl);
        },
        error: (error) => {
          if (error.status === 406) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.domain.assets.asset-collections.public-view.messages.download-size-error');
          }
          else {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message');
          }
        }
      });
    }

  }

  private addUrlParameter(args: BeforeSendEventArgs | BeforeDownloadEventArgs, data: IFileManagerDirectoryContentDTO, baseUrl?: string) {
    (args.ajaxSettings as any).data = JSON.stringify(data);
    const params = new URLSearchParams({
      collectionId: this.assetCollection.id.toString()
    });
    (args.ajaxSettings as any).url = `${baseUrl}?${params.toString()}`;
  }
}
